import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import ArthaMultiSelect from "../../fields/ArthaMultiSelect";
import ArthaCancelButton from "../../fields/ArthaCancelButton";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { handleDatePickerRawChange, dateFormatMaxLength } from "../../fields/datePickerInput";
import AddMember from "./AddMember";
import { ReactComponent as UserImage } from "../../Assets/mobileUser.svg";
import ArthaContinueButton from "../../fields/ArthaContinueButton";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';
import "react-datepicker/dist/react-datepicker.css";
import Checkbox from '@mui/material/Checkbox';
import { orange } from '@mui/material/colors';
import "./dashboard.css";
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  fetchMemberApi,
  generateTimeEntrieseReport,
  fetchOrganizationUsers,
} from "../../actioncreators/actioncreators";
import {
  Search,
  Settings,
  Download,
  ChevronDown,
  ChevronUp,
  Bell,
  Smartphone,
  CheckCircle,
  XCircle,
  LogIn,
  LogOut,
  Coffee,
  Camera,
} from "lucide-react";
import { useSuspension } from "../../contexts/SuspensionContext";

const formatTimestamp = (timestamp) => {
  const date = new Date(parseInt(timestamp));
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
};

const AppUsageRow = ({ rowData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);

  const getStatusIcon = (status) => {
    return status === 'Authorised' ? (
      <CheckCircle size={16} className="text-success" />
    ) : (
      <XCircle size={16} className="text-danger" />
    );
  };

  return (
    <>
      <tr 
        className="border-bottom border-top table-hover rounded-3"
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer' }}
      >
        <td className="px-3 py-3 text-secondary">
          {new Date(rowData.date).toLocaleDateString("en-GB")}
        </td>
        <td className="px-3 py-3 text-secondary">{rowData.totalScreenTime}</td>
        <td className="px-3 py-3 text-secondary">{rowData.authorisedAppUsageTime}</td>
        <td className="px-3 py-3 text-secondary">{rowData.unauthorisedAppUsageTime}</td>
        <td className="px-3 py-3 text-secondary">{rowData.uncategorisedAppUsageTime}</td>
        <td className="px-3 py-3 text-secondary">{rowData.screenUnlocks}</td>
        <td className="px-3 py-3 text-secondary">{rowData.notificationsReceived}</td>
        <td className="px-3 py-3 text-secondary">
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </td>
      </tr>
    </>
  );
};

const formatDateTime = (dateString) => {
  if (!dateString) return "";
  // Use the received ISO string and show only the time part (HH:mm) in UTC as received
  // If you want local time, use getHours/getMinutes; for UTC, use getUTCHours/getUTCMinutes
  const date = new Date(dateString);
  const day = dateString.slice(8, 10);
  const month = dateString.slice(5, 7);
  const year = dateString.slice(0, 4);
  const time = dateString.slice(11, 16); // "HH:mm" from ISO string
  return `${day}/${month}/${year} ${time}`;
};

const TimeEntryRow = ({ entry }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case 'CHECKIN':
        return <LogIn size={16} className="text-success" />;
      case 'CHECKOUT':
        return <LogOut size={16} className="text-danger" />;
      case 'START_BREAK':
      case 'END_BREAK':
        return <Coffee size={16} style={{ color: '#FFA500' }} />;
      case 'WORKPROOF':
        return <Camera size={16} style={{ color: '#0d6efd' }} />;
      default:
        return null;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'CHECKIN':
        return 'text-success';
      case 'CHECKOUT':
        return 'text-danger';
      case 'START_BREAK':
      case 'END_BREAK':
        return 'text-warning';
      case 'WORKPROOF':
        return 'text-primary';
      default:
        return '';
    }
  };

  return (
    <tr className="border-bottom" style={{ transition: 'background-color 0.15s ease-in-out' }}>
      <td className="col-12 col-md-1 px-2 py-2">
        <div className="d-flex align-items-center gap-2">
          {getActivityIcon(entry?.activity_type)}
          <span className={`${getActivityColor(entry?.activity_type)} fw-medium`}>
            {entry?.activity_type}
          </span>
        </div>
      </td>
      {/* Show Start Time only in the correct column */}
      <td className="col-12 col-md-3 px-2 py-2">{formatDateTime(entry?.date_and_time)}</td>
      <td className="col-12 col-md-4 px-2 py-2">{entry?.location_name || ""}</td>
      <td className="col-12 col-md-2 px-2 py-2">
        {entry?.device_details
          ? `${entry.device_details.manufacturer || entry.device_details.deviceManufacturer || ""} ${entry.device_details.deviceName || ""}`.trim()
          : ""}
      </td>
      <td className="col-12 col-md-2 px-2 py-2">{entry?.source || ""}</td>
    </tr>
  );
};

const groupEntriesByDate = (entries) => {
  if (!Array.isArray(entries)) return {};
  const grouped = {};
  entries.forEach(entry => {
    const date = new Date(entry?.date_and_time);
    const formattedDate = date.toLocaleDateString('en-GB'); // This will format as dd/mm/yyyy
    if (!grouped[formattedDate]) {
      grouped[formattedDate] = [];
    }
    grouped[formattedDate].push(entry);
  });
  return grouped;
};

function TimeEntriesReport () {
  // Suspension check
  const { isSuspended } = useSuspension();
  const globalLocationFilter = useSelector((state) => state.selectedLocationFilter);
  const [isReportJustGenerated, setIsReportJustGenerated] = useState(false);
  const [isDownloadEnabled, setIsDownloadEnabled] = useState(false);
  const [hasGeneratedReport, setHasGeneratedReport] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null); // State to track selected member
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState(null); // Replace selectedUsers state with this
  const [users, setUsers] = useState([]); // Add this state for users list
  const [isFirstAccordionOpen, setIsFirstAccordionOpen] = useState(true);
  const [isSecondAccordionOpen, setIsSecondAccordionOpen] = useState(true); // Changed to true
  const [subAccordions, setSubAccordions] = useState({
    first: true,
    second: true,
    third: true,
    fourth: true,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [reportData, setReportData] = useState(null);
  const [includeData, setIncludeData] = useState(false);
  const [openAccordions, setOpenAccordions] = useState({});

  const recordsPerPage = 20;
  const totalPages = reportData?.summaryData?.daily
    ? Math.ceil(reportData.summaryData.daily.length / recordsPerPage)
    : 0;

  const currentTableData = reportData?.summaryData?.daily
    ? reportData.summaryData.daily.slice(
        (currentPage - 1) * recordsPerPage,
        currentPage * recordsPerPage
      )
    : [];

  console.log("currentTableData", currentTableData);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetchOrganizationUsers(globalLocationFilter);
        if (response?.businessStatusCode === 2) {
          const userOptions =
            response.data?.map((user) => ({
              name: user.full_name,
              code: user.user_id,
            })) || [];
          console.log("userOptions", userOptions);
          setUsers(userOptions);
          // Reset selected user when location filter changes
          setSelectedUser(null);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [globalLocationFilter]);

  const handleGenerateReport = async () => {
    if (!selectedUser) {
      toast.error("Please select a user");
      return;
    }

    const payload = {
      fromDate: fromDate.toISOString().split("T")[0],
      toDate: toDate.toISOString().split("T")[0],
      userId: selectedUser.code,
    };

    try {
      const response = await generateTimeEntrieseReport(payload);

      if (response?.businessStatusCode === 2) {
        toast.success("Report generated successfully!");
        // Set the entire response data
        console.log("response", response);
        setReportData(response);
        console.log("reportData", reportData);
        setIsReportJustGenerated(true);
        setIsDownloadEnabled(true); 
        setHasGeneratedReport(true); 
      }
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Failed to generate report");
    }
  };

  useEffect(() => {
    if (isReportJustGenerated) {
      setIsReportJustGenerated(false);
      // Only disable download if we haven't generated a report before
      if (!hasGeneratedReport) {
        setIsDownloadEnabled(false);
      }
    }
  }, [fromDate, toDate, selectedUser]);

  const isFormValid = () => {
    return fromDate && toDate && selectedUser && !isReportJustGenerated;
  };

  const handleDownloadPDF = () => {
    if (!reportData || !reportData.timeEntries || !Array.isArray(reportData.timeEntries) || reportData.timeEntries.length === 0) {
      toast.error("No data available to download");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text("Timesheet Entries Report", pageWidth / 2, 20, { align: "center" });
    
    // User and Date Info
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text(`Employee: ${reportData.fullName || selectedUser.name}`, 14, 35);
    doc.text(`User ID: ${reportData.userId || selectedUser.code}`, 14, 42);
    doc.text(`Period: ${fromDate.toLocaleDateString("en-GB")} - ${toDate.toLocaleDateString("en-GB")}`, 14, 49);
    doc.text(`Generated: ${new Date().toLocaleDateString("en-GB")} ${new Date().toLocaleTimeString()}`, 14, 56);
    
    // Summary Section
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.text("Summary", 14, 69);
    
    const totalDays = reportData.timeEntries ? new Set(reportData.timeEntries.map(entry => 
      new Date(entry?.date_and_time).toLocaleDateString('en-GB'))).size : 0;
    
    const summaryData = [
      ["Total Days", totalDays.toString()],
      ["Total Entries", (reportData.timeEntries?.length || 0).toString()],
      ["From Date", fromDate.toLocaleDateString('en-GB')],
      ["To Date", toDate.toLocaleDateString('en-GB')],
    ];

    autoTable(doc, {
      startY: 75,
      head: [["Metric", "Value"]],
      body: summaryData,
      theme: "grid",
      headStyles: {
        fillColor: [79, 129, 189],
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: "bold",
        halign: "center",
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [60, 60, 60],
      },
      columnStyles: {
        0: { cellWidth: 120, fontStyle: "bold" },
        1: { cellWidth: 60, halign: "center" },
      },
      alternateRowStyles: {
        fillColor: [249, 249, 249],
      },
      margin: { left: 14, right: 14 },
    });

    // Time Entries Section - Grouped by Date
    const groupedEntries = {};
    reportData.timeEntries.forEach(entry => {
      const date = new Date(entry?.date_and_time).toLocaleDateString('en-GB');
      if (!groupedEntries[date]) {
        groupedEntries[date] = [];
      }
      groupedEntries[date].push(entry);
    });

    doc.addPage();
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.text("Time Entries", 14, 20);

    let startY = 28;
    Object.entries(groupedEntries).forEach(([date, entries], index) => {
      // Date header
      if (startY > 250) {
        doc.addPage();
        startY = 20;
      }
      
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.text(`${date} (${entries.length} entries)`, 14, startY);
      startY += 5;

      const entriesData = entries.map((entry) => [
        entry?.activity_type || "",
        new Date(entry?.date_and_time).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }),
        entry?.location_name || "",
        entry?.device_details
          ? `${entry.device_details.manufacturer || entry.device_details.deviceManufacturer || ""} ${entry.device_details.deviceName || ""}`.trim()
          : "",
        entry?.source || "",
      ]);

      autoTable(doc, {
        startY: startY,
        head: [["Activity", "Time", "Location", "Device", "Source"]],
        body: entriesData,
        theme: "grid",
        headStyles: {
          fillColor: [79, 129, 189],
          textColor: [255, 255, 255],
          fontSize: 7,
          fontStyle: "bold",
          halign: "center",
        },
        bodyStyles: {
          fontSize: 7,
          textColor: [60, 60, 60],
        },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 30 },
          2: { cellWidth: 45 },
          3: { cellWidth: 45 },
          4: { cellWidth: 30 },
        },
        alternateRowStyles: {
          fillColor: [249, 249, 249],
        },
        margin: { left: 14, right: 14 },
        didDrawPage: (data) => {
          startY = data.cursor.y + 10;
        }
      });
      
      startY = doc.lastAutoTable.finalY + 10;
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    const userName = (reportData.fullName || selectedUser.name).replace(/\s+/g, "_");
    const fileName = `${userName}_TimeEntries_${fromDate.toISOString().split("T")[0]}_to_${toDate.toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
  };

  const handleApplicationDataChange = (e) => {
    setIncludeData(e.target.checked);
  };
  return (
    <div className="row bg-white rounded-3 shadow pt-3 px-3">
      <div className="col-12 col-md-12 d-flex justify-content-between p-0">
        <h1 className="fw-bold">Timesheet Entries Report</h1>
        <ArthaCancelButton
          className="px-2"
          onClick={isSuspended ? undefined : handleDownloadPDF}
          disabled={!isDownloadEnabled || isSuspended}
          title={isSuspended ? "Download disabled - Organization is suspended" : ""}
          style={{
            opacity: (isDownloadEnabled && !isSuspended) ? 1 : 0.5,
            cursor: (isDownloadEnabled && !isSuspended) ? "pointer" : "not-allowed",
          }}
        >
          <Download size={13} /> &nbsp; Download PDF
        </ArthaCancelButton>
      </div>

      <div className="row p-0 bg-white my-2 pb-1 border rounded-3 d-flex m-0">
        {/* From Date Picker */}
        <div className="col-12 col-md-2 mb-2 pt-2">
          <label className="form-label">From Date</label>
          <DatePicker
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            onChangeRaw={handleDatePickerRawChange("dd/MM/yyyy")}
            className="form-control rounded-3"
            dateFormat="dd/MM/yyyy"
            maxDate={toDate}
            maxLength={dateFormatMaxLength("dd/MM/yyyy")}
            withPortal
          />
        </div>

        {/* To Date Picker */}
        <div className="col-12 col-md-2 mb-2 pt-2">
          <label className="form-label">To Date</label>
          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            onChangeRaw={handleDatePickerRawChange("dd/MM/yyyy")}
            className="form-control rounded-3"
            dateFormat="dd/MM/yyyy"
            maxDate={new Date()}
            minDate={fromDate}
            maxLength={dateFormatMaxLength("dd/MM/yyyy")}
            withPortal
          />
        </div>

        {/* User Selection Dropdown */}
        <div className="col-12 col-md-5 mb-2 pt-2">
          <label className="form-label">Select User</label>
          <Select
            options={users}
            value={selectedUser}
            onChange={setSelectedUser}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.code}
            placeholder="Select a user..."
            className="w-full border rounded-3"
            styles={{
              control: (base) => ({
                ...base,
                height: "32px",
                minHeight: "32px",
                fontSize: "12px",
              }),
              dropdownIndicator: (base) => ({
                ...base,
                padding: "6px",
              }),
              clearIndicator: (base) => ({
                ...base,
                padding: "6px",
              }),
              input: (base) => ({
                ...base,
                margin: "0px",
                padding: "0px",
              }),
              valueContainer: (base) => ({
                ...base,
                padding: "0 6px",
              }),
            }}
          />
        </div>

        {/* Generate Report Button */}
        <div className="col-12 col-md-3 mb-2 d-flex align-items-end">
          <ArthaContinueButton
            onClick={isSuspended ? undefined : handleGenerateReport}
            className="shadow-sm w-100"
            disabled={!isFormValid() || isSuspended}
            title={isSuspended ? "Action disabled - Organization is suspended" : ""}
            style={{
              opacity: (isFormValid() && !isSuspended) ? 1 : 0.5,
              cursor: (isFormValid() && !isSuspended) ? "pointer" : "not-allowed",
            }}
          >
            Generate Report
          </ArthaContinueButton>
        </div>
      </div>

      {reportData && Array.isArray(reportData.timeEntries) && reportData.timeEntries.length > 0 ? (
        <div className="col-12 m-0 p-0 mb-3 bg-white rounded-3">
          <div className="mt-3">
            <div className="accordion" id="timeEntriesAccordion">
              {Object.entries(groupEntriesByDate(reportData.timeEntries)).map(([date, entries], index) => (
                <div className="accordion-item mb-3 rounded-3" key={date}>
                  <div 
                    className="accordion-header d-flex justify-content-between align-items-center border-b border-t px-3 py-2 rounded-3 bg-light cursor-pointer"
                    onClick={() => {
                      setOpenAccordions(prev => ({
                        ...prev,
                        [index]: !prev[index]
                      }));
                    }}
                  >
                    <h6 className="m-0">{date}</h6>
                    <div className="d-flex align-items-center ">
                      <span className="me-2">{entries.length} entries</span>
                      {openAccordions[index] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                  </div>
                  
                  <div className={`accordion-collapse ${openAccordions[index] ? 'show' : 'collapse'}`}>
                    <div className="accordion-body p-0">
                      <div className="table-responsive px-4 " style={{ 
                        maxHeight: '400px', 
                        overflowY: 'auto', 
                        msOverflowStyle: 'none', 
                        scrollbarWidth: 'none' 
                      }}>
                        <table className="table  mb-0 my-3" style={{
                          borderCollapse: 'separate',
                          borderSpacing: 0,
                          border: '1px solid #dee2e6',
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <thead>
                            <tr className="text-secondary" style={{
                              backgroundColor: '#f8f9fa',
                              borderBottom: '2px solid #dee2e6'
                            }}>
                              <th className="px-2 py-2 fw-semibold">Activity</th>
                              <th className="px-2 py-2 fw-semibold">Start Time</th>
                              <th className="px-2 py-2 fw-semibold">Location</th>
                              <th className="px-2 py-2 fw-semibold">Device Details</th>
                              <th className="px-2 py-2 fw-semibold">Source</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entries.map((entry, i) => (
                              <TimeEntryRow 
                                key={i} 
                                entry={entry}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="col-12 col-md-12 mx-auto px-5 py-3 mt-4 rounded-3">
          <UserImage
            alt="Image Not Found"
            className="w-50 h-50 mx-auto px-5 py-2"
          />
          <p className="fw-bold text-center mt-3">
            {reportData && (!Array.isArray(reportData.timeEntries) || reportData.timeEntries.length === 0)
              ? "No time entries found for the selected user and date range."
              : "Kindly generate the report to view it here."
            }
          </p>
        </div>
      )}
    </div>
  );
}

export default TimeEntriesReport;
